import { useEffect, useState, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { 
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer
} from 'recharts';
import { 
  FaBuilding, FaCheck, FaFileInvoiceDollar, FaThLarge, FaChartLine 
} from "react-icons/fa";

const RAW_API_BASE = process.env.REACT_APP_API_BASE || "/api";
const API_BASE = String(RAW_API_BASE).replace(/\/+$/, "");

const STAGE_LEGEND = [
  { name: "Not Started", color: "#6c757d" },
  { name: "Survey", color: "#0d6efd" },
  { name: "Valuation", color: "#0dcaf0" },
  { name: "Negotiation", color: "#6610f2" },
  { name: "Payment", color: "#fd7e14" },
  { name: "Acquired", color: "#198754" },
  { name: "Demolished", color: "#dc3545" },
];

const getStatusColor = (status) => {
    const found = STAGE_LEGEND.find(s => s.name === status);
    return found ? found.color : "#6c757d";
};

// --- Authentication Logic ---
function decodeJwt(token) {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

// --- Helper: Currency Formatter ---
const formatCurrency = (value) => {
  if (!value) return "$0";
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
};

// --- Helper: Timestamp Formatter (Date + Time) ---
const formatTimestamp = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    // Returns format: YYYY-MM-DD HH:MM
    const datePart = d.toISOString().split('T')[0];
    const timePart = d.toTimeString().split(' ')[0].substring(0, 5);
    return `${datePart} ${timePart}`;
};

function Homepage() {
  const [info, setInfo] = useState({ name: "", email: "", role: "", organization: "" });
  
  // State for real API data
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- 1. User Info Effect ---
  useEffect(() => {
    const token = localStorage.getItem("token") || "";
    const nameLS = localStorage.getItem("name") || "";
    const orgLS = localStorage.getItem("userOrganization") || localStorage.getItem("organization") || "";

    let email = localStorage.getItem("rememberEmail") || "";
    let role = "";

    const decoded = token ? decodeJwt(token) : null;
    if (decoded) {
      email = decoded.email || email;
      role = decoded.role || "";
    }

    const friendlyName = nameLS || (email ? email.split("@")[0] : "User");
    setInfo({
      name: friendlyName,
      email: email || "",
      role,
      organization: orgLS || "",
    });
  }, []);

  // --- 2. Fetch Data Effect (Optimized: Fetch list, then enrich top 10) ---
  useEffect(() => {
    const fetchLocations = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        // Step A: Fetch Basic List
        const res = await fetch(`${API_BASE}/locations`, { 
            headers: { Authorization: `Bearer ${token}` } 
        });
        
        if (res.ok) {
          const rawData = await res.json();
          let allData = Array.isArray(rawData) ? rawData : [];

          // Step B: Sort to find the Top 10 most recent
          const sortedIndices = allData
            .map((loc, index) => ({ loc, index }))
            .sort((a, b) => {
                const dateA = new Date(a.loc.updated_at || a.loc.created_at || 0);
                const dateB = new Date(b.loc.updated_at || b.loc.created_at || 0);
                return dateB - dateA; 
            });
          
          // Get the top 10 items that need detailed addresses
          const top10Wrappers = sortedIndices.slice(0, 10);

          // Step C: Fetch Details ONLY for these 10 items (Parallel)
          const enrichedTop10 = await Promise.all(top10Wrappers.map(async (wrapper) => {
             const { loc } = wrapper;
             try {
                // Fetch full details to get the address
                const detailRes = await fetch(`${API_BASE}/location/${encodeURIComponent(loc.building_id)}`, {
                    headers: { Authorization: `Bearer ${token}` } 
                });
                if(detailRes.ok) {
                    const detail = await detailRes.json();
                    // Merge detail info into the list item
                    return {
                        ...loc,
                        address: detail.address || detail.location_name || loc.address || "",
                        updated_at: detail.updated_at || loc.updated_at,
                        created_at: detail.created_at || loc.created_at
                    };
                }
             } catch(e) { 
                 console.warn("Failed to fetch detail for dashboard item", loc.building_id);
             }
             return loc; // Fallback to original if fetch fails
          }));

          // Step D: Merge enriched items back into the main list
          // We create a Map of enriched items for O(1) lookup
          const enrichedMap = new Map(enrichedTop10.map(item => [item.uuid || item.building_id, item]));

          const finalData = allData.map(item => {
              const key = item.uuid || item.building_id;
              return enrichedMap.has(key) ? enrichedMap.get(key) : item;
          });

          setLocations(finalData);
        } else {
          console.warn("Failed to fetch locations for dashboard");
        }
      } catch (err) {
        console.error("Dashboard fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  // --- 3. Compute Statistics (useMemo for performance) ---
  const dashboardStats = useMemo(() => {
    if (!locations.length) return null;

    // A. Summary Cards
    const totalBuildings = locations.length;
    
    const acquiredCount = locations.filter(l => 
        l.current_status === "Acquired" || l.current_status === "Demolished"
    ).length;

    const completionRate = totalBuildings > 0 
        ? ((acquiredCount / totalBuildings) * 100).toFixed(1) 
        : 0;

    const totalBudget = locations.reduce((acc, curr) => {
        const val = parseFloat(curr.valuation || curr.budget || curr.price || 0);
        return acc + (isNaN(val) ? 0 : val);
    }, 0);

    // B. Pie Chart Data
    const statusCounts = {};
    locations.forEach(loc => {
        const status = loc.current_status || "Unknown";
        statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    const pieData = STAGE_LEGEND.map(stage => ({
        name: stage.name,
        value: statusCounts[stage.name] || 0,
        color: stage.color
    })).filter(item => item.value > 0);

    // C. Recent Progress List (Sorted by updated_at desc)
    const recentUpdates = [...locations]
        .sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at || 0);
            const dateB = new Date(b.updated_at || b.created_at || 0);
            return dateB - dateA; 
        })
        .slice(0, 10); 

    return {
        totalBuildings,
        acquiredCount,
        completionRate,
        totalBudget,
        pieData,
        recentUpdates
    };

  }, [locations]);

  const StatCard = ({ title, value, icon, colorClass, bgClass }) => (
    <div className="col-md-6 col-lg-3 mb-4">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body d-flex align-items-center justify-content-between p-4">
          <div>
            <p className="text-muted fw-semibold mb-1 small">{title}</p>
            <h3 className="fw-bold mb-0">{value}</h3>
          </div>
          <div className={`rounded p-3 ${bgClass}`}>
            <span className={`h4 mb-0 ${colorClass}`}>{icon}</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) return <div className="d-flex justify-content-center align-items-center min-vh-100"><div className="spinner-border text-primary" role="status"></div></div>;

  const stats = dashboardStats || {
      totalBuildings: 0,
      acquiredCount: 0,
      completionRate: "0.0",
      totalBudget: 0,
      pieData: [],
      recentUpdates: []
  };

  return (
    <div className="min-vh-100 d-flex flex-column bg-light">
      {/* Topbar */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom shadow-sm sticky-top">
        <div className="container-fluid px-4">
          <span className="navbar-brand fw-bold text-primary">PropertyManager</span>
          
          <div className="d-flex align-items-center gap-3 ms-auto">
            <div className="d-none d-md-block text-end lh-1">
              <div className="fw-bold text-dark">{info.name}</div>
              <div className="small text-muted" style={{ fontSize: '0.75rem' }}>
                {info.organization} {info.role ? `• ${info.role}` : ''}
              </div>
            </div>
            <button className="btn btn-outline-danger btn-sm" onClick={logout}>
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Main Dashboard Content */}
      <main className="container-fluid px-4 py-4">
        
        {/* Section 1: Summary Statistics */}
        <div className="row">
          <StatCard 
            title="Total Buildings" 
            value={stats.totalBuildings} 
            icon={<FaBuilding />} 
            bgClass="bg-primary-subtle" 
            colorClass="text-primary" 
          />
          <StatCard 
            title="Acquired / Done" 
            value={stats.acquiredCount} 
            icon={<FaCheck />} 
            bgClass="bg-success-subtle" 
            colorClass="text-success" 
          />
          <StatCard 
            title="Total Valuation" 
            value={formatCurrency(stats.totalBudget)} 
            icon={<FaFileInvoiceDollar />} 
            bgClass="bg-info-subtle" 
            colorClass="text-info" 
          />
          <StatCard 
            title="Completion Rate" 
            value={`${stats.completionRate}%`} 
            icon={<FaThLarge />} 
            bgClass="bg-warning-subtle" 
            colorClass="text-warning" 
          />
        </div>

        {/* Section 2: Charts & Recent Progress */}
        <div className="row">
          
          {/* A. Project Status Distribution (Pie Chart) */}
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <h5 className="card-title fw-bold mb-4">Project Status Distribution</h5>
                <div style={{ height: '300px', width: '100%' }}>
                  {stats.pieData.length > 0 ? (
                    <ResponsiveContainer>
                        <PieChart>
                        <Pie
                            data={stats.pieData}
                            innerRadius={80}
                            outerRadius={110}
                            paddingAngle={2}
                            dataKey="value"
                        >
                            {stats.pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => [`${value} Sites`, 'Count']} />
                        </PieChart>
                    </ResponsiveContainer>
                  ) : (
                      <div className="d-flex h-100 align-items-center justify-content-center text-muted">No Data Available</div>
                  )}
                </div>
                {/* Custom Legend */}
                <div className="d-flex flex-wrap justify-content-center gap-3 mt-3">
                  {stats.pieData.map((item) => (
                    <div key={item.name} className="d-flex align-items-center small">
                      <span 
                        style={{ width: 10, height: 10, backgroundColor: item.color, display: 'inline-block', marginRight: 5, borderRadius: '50%' }} 
                      ></span>
                      <span className="text-muted">{item.name}: <strong>{item.value}</strong></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* B. Recent Progress List */}
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-4 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title fw-bold mb-0">
                        <FaChartLine className="text-warning me-2" />
                        Recent Progress
                    </h5>
                    <span className="small text-muted"><i className="bi bi-clock me-1"></i>Last 30 Days</span>
                </div>
                
                {/* Scrollable List Container */}
                <div 
                    className="flex-grow-1 overflow-auto pe-2" 
                    style={{ maxHeight: '400px', scrollbarWidth: 'thin' }}
                >
                    {stats.recentUpdates.length > 0 ? (
                        <div className="d-flex flex-column gap-3">
                            {stats.recentUpdates.map((loc) => {
                                const statusColor = getStatusColor(loc.current_status);
                                return (
                                    <div key={loc.uuid || loc.building_id} className="d-flex gap-3 position-relative">
                                        {/* Colored Dot Indicator */}
                                        <div className="pt-1">
                                            <div 
                                                style={{ 
                                                    width: '10px', 
                                                    height: '10px', 
                                                    borderRadius: '50%', 
                                                    backgroundColor: statusColor 
                                                }} 
                                            />
                                        </div>
                                        
                                        {/* Content */}
                                        <div className="flex-grow-1 pb-3 border-bottom border-light">
                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                <h6 className="fw-bold mb-0 text-dark">{loc.building_id}</h6>
                                                {/* TIMESTAMP DISPLAY */}
                                                <small className="text-muted" style={{fontSize: '0.75rem', fontWeight: 600}}>
                                                    {formatTimestamp(loc.updated_at || loc.created_at)}
                                                </small>
                                            </div>
                                            
                                            {/* ADDRESS DISPLAY */}
                                            <div className="text-muted small mb-2 text-truncate" style={{maxWidth: '90%'}}>
                                                {loc.address ? loc.address : <span className="fst-italic opacity-50">No address provided</span>}
                                            </div>

                                            <div 
                                                className="badge bg-white border shadow-sm text-dark px-2 py-1 fw-normal"
                                                style={{ fontSize: '0.75rem' }}
                                            >
                                                Updated to: <span style={{ color: statusColor, fontWeight: 700 }}>{loc.current_status}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="d-flex h-100 align-items-center justify-content-center text-muted">
                            No recent updates found.
                        </div>
                    )}
                </div>

              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}

export default Homepage;